import React from 'react';
import { clsx } from 'clsx';
import { Card, CardContent } from '../../../components/ui/Card';

/** Dashed empty state — matches AssignmentProgressBubbly / StudentProgressAnalyticsPanel. */
export const DASHBOARD_EMPTY_CLASS =
  'rounded-2xl border border-dashed border-purple-300 bg-purple-50/60 px-4 py-8 text-center text-sm text-slate-600 dark:border-purple-500/35 dark:bg-white/[0.04] dark:text-slate-400';

export function DashboardEmptyState({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return <div className={clsx(DASHBOARD_EMPTY_CLASS, className)}>{children}</div>;
}

type SkeletonVariant = 'card' | 'kpi' | 'hero' | 'list';

export function DashboardCardSkeleton({
  variant = 'card',
  className,
}: {
  variant?: SkeletonVariant;
  className?: string;
}) {
  if (variant === 'kpi') {
    return (
      <Card
        className={clsx(
          'border-purple-200/60 dark:border-purple-500/25 bg-white dark:bg-gray-800 animate-pulse',
          className
        )}
        aria-busy="true"
      >
        <CardContent className="p-4 sm:p-5 space-y-3">
          <div className="h-9 w-9 rounded-xl bg-slate-200 dark:bg-gray-700" />
          <div className="h-7 w-16 bg-slate-200 dark:bg-gray-700 rounded" />
          <div className="h-3 w-24 bg-slate-100 dark:bg-gray-700/80 rounded" />
        </CardContent>
      </Card>
    );
  }

  if (variant === 'hero') {
    return (
      <div
        className={clsx(
          'mb-6 rounded-[1.75rem] border border-purple-200/80 bg-white dark:bg-gray-800 p-7 animate-pulse',
          className
        )}
        aria-busy="true"
      >
        <div className="flex gap-4">
          <div className="h-16 w-16 rounded-full bg-slate-200 dark:bg-gray-700 shrink-0" />
          <div className="flex-1 space-y-2">
            <div className="h-4 w-28 bg-slate-200 dark:bg-gray-700 rounded" />
            <div className="h-8 w-48 bg-slate-200 dark:bg-gray-700 rounded" />
            <div className="h-3 w-20 bg-slate-100 dark:bg-gray-700/80 rounded" />
          </div>
        </div>
      </div>
    );
  }

  if (variant === 'list') {
    return (
      <Card
        className={clsx('border-purple-200/60 dark:border-purple-500/25 animate-pulse', className)}
        aria-busy="true"
      >
        <CardContent className="p-5 space-y-4">
          <div className="h-5 w-36 bg-slate-200 dark:bg-gray-700 rounded" />
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="flex gap-3">
              <div className="h-9 w-9 rounded-xl bg-slate-100 dark:bg-gray-700/80 shrink-0" />
              <div className="flex-1 space-y-2">
                <div className="h-3 w-24 bg-slate-100 dark:bg-gray-700/80 rounded" />
                <div className="h-4 w-full max-w-xs bg-slate-200 dark:bg-gray-700 rounded" />
              </div>
            </div>
          ))}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card
      className={clsx(
        'border-purple-200/60 dark:border-purple-500/25 bg-white dark:bg-gray-800 animate-pulse min-h-[10rem]',
        className
      )}
      aria-busy="true"
    >
      <CardContent className="p-5 space-y-3">
        <div className="h-5 w-40 bg-slate-200 dark:bg-gray-700 rounded" />
        <div className="h-20 bg-slate-100 dark:bg-gray-700/80 rounded-xl" />
      </CardContent>
    </Card>
  );
}

export function DashboardInlineError({
  message,
  onRetry,
}: {
  message: string;
  onRetry?: () => void;
}) {
  return (
    <div className={DASHBOARD_EMPTY_CLASS}>
      <p>{message}</p>
      {onRetry ? (
        <button
          type="button"
          onClick={onRetry}
          className="mt-3 inline-flex min-h-[44px] items-center justify-center rounded-full border border-purple-300 bg-white px-4 py-2 text-sm font-semibold text-purple-700 hover:bg-purple-50 dark:border-purple-500/40 dark:bg-gray-800 dark:text-purple-200 dark:hover:bg-purple-950/30"
        >
          Retry
        </button>
      ) : null}
    </div>
  );
}
